export default `# Movies

Do you remember the \`MoviesList\` from the first chapter? It used the \`map\` method to iterate through an array of movie titles and display them on the screen.

\`\`\`react
const titles = ["Breaking bad", "Narcos", "Game of Thrones"];

const MovieListItem = ({ title }) => (
  <li>{title}</li>
)

const MovieList = ({ titles }) => (
  <ul>
    {
      titles.map( (title, index) => (
        <MovieListItem key={index.toString()} title={title} />
      ))
    }
  </ul>
)

render(
  <MovieList titles={titles} />
)
\`\`\`

Let's change its name from \`MoviesList\` to \`Movies\` and make it a class component. You should place the code in the *./main/movies/Movies.js*.

Now, create the \`MovieListItem\` in a separate file called *MovieListItem.js* in the same directory. And, finally, create an accompanying
*MovieListItem.css*:

\`\`\`plainJavascript
// MovieListItem.js

import React from "react";
import "./MovieListItem.css";

const MovieListItem = ({ title }) => (
  <li className="movie-item">{title}</li>
);

export default MovieListItem;
\`\`\`

\`\`\`plainJavascript
// Movies.js

import React from "react";
import "./Movies.css";
import MovieListItem from "./MovieListItem";

const movies = ["Breaking bad", "Narcos", "Game of Thrones"];

class Movies extends React.Component {
  render() {
    return (
      <section>
        <ul className="movies">
          {
            movies.map( (title, index) => (
              <MovieListItem key={index.toString()} title={title} />
            ))
          }
        </ul>
      </section>
    )
  }
}

export default Movies;
\`\`\`

At this point, our application should look like the one below:

\`\`\`codeSandbox
1v7z27zrx4
\`\`\`

Now, you are ready to start fetching data from the TMDB API.

Add the \`componentDidMount\` method to the \`MovieList\` component.

The \`componentDidMount\` method is called **after** the component is rendered.
This is a perfect place to fetch movies:

\`\`\`plainJavascript
// Movies.js

import React from "react";
import "./Movies.css";
import MovieListItem from "./MovieListItem";

const movies = ["Breaking bad", "Narcos", "Game of Thrones"];

class Movies extends React.Component {
  componentDidMount() {
    const apiUrl = \`https://api.themoviedb.org/3/discover/movie?api_key=\${process.env.REACT_APP_TMDB_API_KEY}&language=en-US&sort_by=popularity.desc&include_adult=false&include_video=false&page=1\`

    fetch(apiUrl)
      .then(response => response.json())
      .then(data => console.log(data))
      .catch(error => console.log(error))

    console.log("Before or after data?");
  }

  render() {
    return (
      <section>
        <ul className="movies">
          {
            movies.map( (title, index) => (
              <MovieListItem key={index.toString()} title={title} />
            ))
          }
        </ul>
      </section>
    )
  }
}

export default Movies;
\`\`\`

To fetch the movies, you have to pass an API url to the \`fetch\` method.
In the \`componentDidMount\` method, I assigned the string literal to the \`apiUrl\` with an embedded API key.
You have access to it via the *environmental variable* \`process.env.REACT_APP_TMDB_API_KEY\`.

The \`fetch\` method calls The Movie DB and requests movie data. It takes some time for the API to respond. Therefore \`fetch\`
returns the *Promise*.

When the Promise *resolves* – meaning the request was successful – it returns the *Response* object.

The *Response* object has a \`json\` method that converts the raw data received from the API to the JavaScript object. But it doesn't return an object immediately.
It returns another *Promise* that resolves to the JavaScript object. That's why we have to chain another \`then\` method,
one that takes this object as an argument – \`data\` – and logs it to the console.

At the end of the chain, I added the \`catch\` method that gets an \`error\` if the *Promise* has been *rejected* (failed).
In such case, we will log the \`error\` to the console.

It may feel a bit mind-bending to understand asynchronous functions and *Promises*.
The most important point here is that it takes some time for the API to respond.
And we don't want to block the execution of the application while waiting for the response.

I added another \`console.log\` below the \`fetch\` method. If the \`fetch\` function was synchronous, it would block the execution of this \`console.log\`.
But because it's an asynchronous operation, it will print to the console first even though it comes **after** the \`fetch\`.

When you add the code to the application and open Developers Tools, check the *Console* tab.

You should see similar results. When you expand the results you will see that we received an array containing 20 movies:

![API response in the console](https://sweetpumpkins.codecamps.com/api-response-with-movies-min.jpg)

## Extracting Data

Now we have our data! Let's store them in the \`state\` - the component's short-term memory. Which data should we put in there?

Let's save: *vote_count*, *id*, *genre_ids* array, *poster_path*, *title*, *vote_average* and the *release_date*

Instead of using the \`console.log\` in the second \`then\`, we will replace it with the \`storeMovies\` method that will accept the \`data\` as an argument
and store it in the \`state\`.

You can also remove the last \`console.log\` instruction that was used to prove that *Promises* work as expected.

Create-react-app uses Babel to transpile the ES6 and some of the new features of ES7 code to ES5.
Even though not all of the ES7 features have become standard, we can still use them.

In ES7, we can use a new, simpler syntax, both to initialise the class properties and to declare class methods, using arrow functions.
Previously, to initialise the \`state\` and to bind the class method, we had to do it in the constructor:

\`\`\`plainJavascript
class Movies extends React.Component {
  constructor(super) {
    super(props);
    this.state = {
      movies: []
    }
    this.storeMovies = this.storeMovies.bind(this);
  }

  storeMovies(data) {
    // some code
  }
}
\`\`\`

Thanks to ES7, we can skip the constructor and use a simpler syntax to declare methods:

\`\`\`plainJavascript
class MovieList extends React.Component {
  state = {
    movies: []
  }

   storeMovies = data => {
     // some code
   }
}
\`\`\`

Here is the implementation. Let's analyse it:

\`\`\`plainJavascript
// Movies.js

import React from "react";
import "./Movies.css";
import MovieListItem from "./MovieListItem";

const movies = ["Breaking bad", "Narcos", "Game of Thrones"];

class Movies extends React.Component {
  state = {
    movies: []
  }

  componentDidMount() {
    const apiUrl = \`https://api.themoviedb.org/3/discover/movie?api_key=\${process.env.REACT_APP_TMDB_API_KEY}&language=en-US&sort_by=popularity.desc&include_adult=false&include_video=false&page=1\`
    fetch(apiUrl)
      .then(response => response.json())
      .then(data => this.storeMovies(data))
      .catch(error => console.log(error))
  }

  storeMovies = data => {
    const movies = data.results.map( result => {
      const  { vote_count, id, genre_ids, poster_path, title, vote_average, release_date } = result;
      return { vote_count, id, genre_ids, poster_path, title, vote_average, release_date };
    });

    this.setState({ movies })
  }

  render() {
    return (
      <section>
        <ul className="movies">
          {
            movies.map( (title, index) => (
              <MovieListItem key={index.toString()} title={title} />
            ))
          }
        </ul>
      </section>
    )
  }
}

export default Movies;
\`\`\`

We initialised the components \`state\` with one property – \`movies\` (an empty array). This is where we will store the movies data.

Next, we will fetch movies using the \`fetch\` method in the \`componentDidMount\` method. When we receive the data, we will pass it to the \`storeMovies\` method.
The \`storeMovies\` stores the data in \`state\`.

Let's analyze the \`storeMovies\` method in details:

- It creates a \`movies\` variable and assigns to it the result of iteration through the \`data.results\` array
- Inside the iterating function, we destructure every \`result\` object and we assign some of its properties to respective variables. In the next step, we return the variables as one object.

Finally, the \`movies\` array is stored to the state using the \`setState\` method.

Now, when you inspect the \`Movies\` component in the React tab of Developer Tools, you should see all the movies pulled from the API in \`Movies\` state.

![Movies from API in state](https://sweetpumpkins.codecamps.com/movies-in-state-min.jpg)

## Displaying Movies

Until now, the \`Movies\` component has iterated through the \`movies\` array that is hard-coded at the top of *Movies.js*.

Now, you can remove this array and use real data from \`this.state.movies\`.

On every iteration, the next movie will be passed to the \`MovieListItem\`. While iterating through the \`this.state.movies\` array,
we will now pass the \`movie\` object rather than the \`title\`.

Every \`movie\` has a unique \`id\` and we can use it as a \`key\`:

\`\`\`plainJsx
class Movies extends React.Component {
  state = {
    movies: []
  }

  // omited coded

  render() {
    return (
      <section>
        <ul className="movies">
          {
            this.state.movies.map( movie => (
              <MovieListItem key={movie.id} movie={movie} />
            ))
          }
        </ul>
      </section>
    )
  }
}
\`\`\`

At the same time, we have to adjust the \`MovieListItem\` a bit. It now receives a \`movie\` object, not just a \`title\`, for example:

\`\`\`plainJavascript
movie = {
  genre_ids: Array[18, 14, 27, 53],
  id: 346364,
  poster_path: "/9E2y5Q7WlCVNEhP5GiVTjhEhx1o.jpg",
  title: "It",
  vote_average: 7.3,
  vote_count: 3971,
  release_date: "2014-09-10"
}
\`\`\`

First, let's use just the \`title\` and \`poster_path\`.

From the API [documentation](https://developers.themoviedb.org/3/getting-started/images), we can see that to create a full URL of an image we have to concatenate:
- base_url: \`https://image.tmdb.org/t/p/\`
- file_size: one of *w92*, *w154*, *w185*, *w342*, *w500* or *original*
- file_path: our *poster_path*

Adding a string is called *concatenation*. Before ES6 we would write:

\`\`\`plainJavascript
const imageUrl = "https://image.tmdb.org/t/p/" + "w342/" + poster_path
\`\`\`

With the ES6, we can use a string literal:
\`\`\`plainJavascript
const imageUrl = \`https://image.tmdb.org/t/p/w342/\${poster_path}\`
\`\`\`

And here is our \`MovieListItem\`:

\`\`\`plainJsx
// MovieListItem.js

import React from "react";
import "./MovieListItem.css";

const MovieListItem = ({ movie }) => {
  const { title, poster_path } = movie;
  const imgUrl = \`https://image.tmdb.org/t/p/w342/\${poster_path}\`;

  return (
    <li className="movie-item">
      <img src={imgUrl} alt=""/>
      <span>{title}</span>
    </li>
  )
}

export default MovieListItem;
\`\`\`

It takes the \`movie\` object and displays the movie's poster and title.

In the original version of the \`MovieListItem\`, we were returning the elements to display immediately:

\`\`\`plainJavascript
const MovieListItem = ({ movie }) => (
    <li className="movie">
      // some code
    </li>
);
\`\`\`

Now, we need to change the body of the function, so that we can make some preparations before we return what we want to display. That's why
we had to change the \`MovieListItem\`structure to:

\`\`\`plainJavascript
const MovieListItem = ({ movie }) => {
  // do something

  return (
    // our component
  )
}
\`\`\`

First, we are destructuring the \`movie\` object to get the \`title\` and \`poster_path\` properties. Next, we create the full \`imgUrl\` and – finally – return
the component.

Our application fetches 20 movies and displays them. In the next chapter, we will style it properly.

Here is the final code. In the code sandboxes I had to put the API key, but you should use the evironmental variable (\`process.env.REACT_APP_TMDB_API_KEY\`).

\`\`\`codeSandbox
ql8m0j6k8j
\`\`\`

`;



// WEBPACK FOOTER //
// ./src/book/chapters/app-movies.js